import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";

import { useTheme } from "../../contexts/ThemeContext";
import { useAppDispatch, useAppSelector } from "../../redux";
import { AnalyticsTimeframe, clearAnalytics, getAgentAnalytics } from "../../redux/reducers/agentAnalyticsSlice";
import { cn } from "../../utils/cn";
import { Button, Card, DateField, DropdownField, Spinner, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { ANALYTICS_COLORS, AnalyticsChartTooltip, DashboardPanel, LegendItem, MetricCard } from "./analytics/AnalyticsWidgets";
import { buildPreviewAnalytics, compareHalves, fillMissingAnalyticsBuckets, getAnalyticsBucketSize, toDashboardPoints } from "./analytics/analyticsData";

const timeframeOptions: { label: string; value: AnalyticsTimeframe }[] = [
  { value: "15m", label: "Past 15 minutes" },
  { value: "30m", label: "Past 30 minutes" },
  { value: "1h", label: "Past 1 hour" },
  { value: "3h", label: "Past 3 hours" },
  { value: "6h", label: "Past 6 hours" },
  { value: "12h", label: "Past 12 hours" },
  { value: "24h", label: "Past 24 hours" },
  { value: "3d", label: "Past 3 days" },
  { value: "7d", label: "Past 7 days" },
  { value: "30d", label: "Past 30 days" },
  { value: "custom", label: "Custom range" },
];

const validTimeframes = timeframeOptions.map(({ value }) => value);

const getBrowserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const isValidTimezone = (timezone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

const formatMilliseconds = (value: number): string => {
  if (!value) return "0ms";
  if (value >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${Math.round(value)}ms`;
};

const formatMinutes = (value: number): string => {
  if (!value) return "0m";
  if (value < 1) return "<1m";
  if (value < 60) return `${value.toFixed(1)}m`;
  const totalMinutes = Math.round(value);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

const formatPercent = (value: number): string => `${value.toFixed(2)}%`;

const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
};

const localDateToUtcEpoch = (dateString: string, isEndOfDay = false): number => {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setHours(isEndOfDay ? 23 : 0, isEndOfDay ? 59 : 0, isEndOfDay ? 59 : 0, isEndOfDay ? 999 : 0);
  return date.getTime();
};

const toCalendarDate = (timestamp: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) return null;
  const [year, month, day] = timestamp.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatAxisTime = (timestamp: string, timeframe: AnalyticsTimeframe, timezone: string): string => {
  const calendarDate = toCalendarDate(timestamp);
  if (calendarDate) return calendarDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;

  if (["15m", "30m", "1h", "3h", "6h", "12h", "24h"].includes(timeframe)) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: timezone });
  }
  if (timeframe === "3d") {
    return date.toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZone: timezone });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: timezone });
};

const formatTooltipTime = (timestamp: string, timezone: string): string => {
  const calendarDate = toCalendarDate(timestamp);
  if (calendarDate) return calendarDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: timezone });
};

type HealthStatus = "healthy" | "warning" | "critical";

type HealthMetricRowProps = {
  label: string;
  value: string;
  threshold: string;
  status: HealthStatus;
  score: number;
};

const HealthMetricRow = ({ label, value, threshold, status, score }: HealthMetricRowProps) => {
  const statusLabel = status === "healthy" ? "Healthy" : status === "warning" ? "Watch" : "Alert";
  const statusClasses = {
    healthy: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    warning: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    critical: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  };
  const segmentClasses = {
    healthy: "bg-emerald-500 dark:bg-emerald-400",
    warning: "bg-amber-500 dark:bg-amber-400",
    critical: "bg-red-500 dark:bg-red-400",
  };

  return (
    <View className="border-b border-gray-100 py-3 last:border-b-0 dark:border-gray-800">
      <View className="flex items-start justify-between gap-3">
        <View className="min-w-0">
          <Text className="block truncate text-xs font-medium text-gray-800 dark:text-gray-200">{label}</Text>
          <Text className="mt-0.5 block text-[10px] text-gray-400 dark:text-gray-500">Target {threshold}</Text>
        </View>
        <View className="text-right">
          <Text className="block text-xs font-semibold tabular-nums text-gray-950 dark:text-white">{value}</Text>
          <Text className={cn("mt-1 inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide", statusClasses[status])}>
            {statusLabel}
          </Text>
        </View>
      </View>
      <View className="mt-2 grid grid-cols-5 gap-1">
        {Array.from({ length: 5 }, (_, index) => (
          <View
            key={index}
            className={cn("h-1 rounded-full", index < score ? segmentClasses[status] : "bg-gray-100 dark:bg-gray-800")}
          />
        ))}
      </View>
    </View>
  );
};

const getHealthStatus = (value: number, threshold: number): HealthStatus => {
  if (value <= threshold) return "healthy";
  if (value <= threshold * 1.25) return "warning";
  return "critical";
};

const getHealthScore = (value: number, threshold: number): number => {
  if (value <= threshold) return 5;
  if (value <= threshold * 1.1) return 4;
  if (value <= threshold * 1.25) return 3;
  if (value <= threshold * 1.5) return 2;
  return 1;
};

const DashboardSkeleton = () => (
  <View className="min-h-full bg-gray-50 p-4 dark:bg-slate-950">
    <View className="mb-4 flex items-center justify-between">
      <View className="space-y-2">
        <View className="h-5 w-48 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
        <View className="h-3 w-72 animate-pulse rounded bg-gray-100 dark:bg-gray-800/70" />
      </View>
      <Spinner size={22} />
    </View>
    <View className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {Array.from({ length: 6 }, (_, index) => (
        <Card
          key={index}
          className="h-[126px] animate-pulse border-gray-200 bg-white shadow-none dark:border-gray-800 dark:bg-slate-900"
        />
      ))}
    </View>
    <View className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-12">
      <Card className="h-[370px] animate-pulse border-gray-200 bg-white shadow-none lg:col-span-8 dark:border-gray-800 dark:bg-slate-900" />
      <Card className="h-[370px] animate-pulse border-gray-200 bg-white shadow-none lg:col-span-4 dark:border-gray-800 dark:bg-slate-900" />
    </View>
  </View>
);

type DashboardErrorStateProps = {
  message: string;
  onRetry: () => void;
};

const DashboardErrorState = ({ message, onRetry }: DashboardErrorStateProps) => (
  <View className="flex min-h-full items-center justify-center bg-gray-50 p-6 dark:bg-slate-950">
    <Card className="w-full max-w-lg border-gray-200 bg-white p-6 text-center shadow-none dark:border-gray-800 dark:bg-slate-900">
      <View className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/40">
        <Icon
          name="error"
          size={20}
          className="text-red-600 dark:text-red-400"
        />
      </View>
      <Text className="block text-sm font-semibold text-gray-900 dark:text-white">Unable to load analytics</Text>
      <Text className="mx-auto mt-1 block max-w-sm text-xs text-gray-500 dark:text-gray-400">{message}</Text>
      <Button
        variant="info"
        size="sm"
        onClick={onRetry}
        className="mt-4"
      >
        <Icon
          name="refresh"
          size={13}
        />
        Try again
      </Button>
    </Card>
  </View>
);

export const AgentAnalytics = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId = "preview-agent" } = useParams<{ agentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { currentAnalytics, isLoading, error } = useAppSelector((state) => state.agentAnalytics);

  const requestedTimeframe = searchParams.get("timeframe") as AnalyticsTimeframe;
  const selectedTimeframe = validTimeframes.includes(requestedTimeframe) ? requestedTimeframe : "24h";
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const timezoneParam = searchParams.get("timezone");
  const timezone = timezoneParam && isValidTimezone(timezoneParam) ? timezoneParam : getBrowserTimezone();

  const [pendingStartDate, setPendingStartDate] = useState(startDate);
  const [pendingEndDate, setPendingEndDate] = useState(endDate);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [latencyView, setLatencyView] = useState<"end-to-end" | "pipeline">("end-to-end");
  const [hasRequested, setHasRequested] = useState(false);
  const isCustomDateRangeInvalid = Boolean(pendingStartDate && pendingEndDate && pendingStartDate > pendingEndDate);

  useEffect(() => {
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
  }, [endDate, startDate]);

  const fetchAnalytics = useCallback(async () => {
    if (!agentId || !timezoneParam) return;
    if (selectedTimeframe === "custom" && (!startDate || !endDate || startDate > endDate)) return;

    setHasRequested(true);
    const result = await dispatch(
      getAgentAnalytics({
        agentId,
        timeframe: selectedTimeframe,
        ...(selectedTimeframe === "custom" && {
          startDate: localDateToUtcEpoch(startDate),
          endDate: localDateToUtcEpoch(endDate, true),
        }),
        ...(["3d", "7d", "30d", "custom"].includes(selectedTimeframe) && { timezone }),
      }),
    );
    if (getAgentAnalytics.fulfilled.match(result)) setLastUpdated(new Date());
  }, [agentId, dispatch, endDate, selectedTimeframe, startDate, timezone, timezoneParam]);

  useEffect(() => {
    const currentTimezone = searchParams.get("timezone");
    if (currentTimezone && isValidTimezone(currentTimezone)) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("timeframe", selectedTimeframe);
    nextParams.set("timezone", getBrowserTimezone());
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, selectedTimeframe, setSearchParams]);

  useEffect(() => {
    void fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (!isAutoRefreshEnabled) return;
    const interval = window.setInterval(() => void fetchAnalytics(), 60_000);
    return () => window.clearInterval(interval);
  }, [fetchAnalytics, isAutoRefreshEnabled]);

  useEffect(() => {
    return () => {
      dispatch(clearAnalytics());
    };
  }, [dispatch]);

  const handleTimeframeChange = (timeframe: AnalyticsTimeframe) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("timeframe", timeframe);
    nextParams.set("timezone", getBrowserTimezone());
    if (timeframe !== "custom") {
      nextParams.delete("startDate");
      nextParams.delete("endDate");
      setPendingStartDate("");
      setPendingEndDate("");
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleApplyCustomRange = () => {
    if (!pendingStartDate || !pendingEndDate || pendingStartDate > pendingEndDate) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("timeframe", "custom");
    nextParams.set("timezone", getBrowserTimezone());
    nextParams.set("startDate", pendingStartDate);
    nextParams.set("endDate", pendingEndDate);
    setSearchParams(nextParams, { replace: true });
  };

  const handleRefresh = async () => {
    await fetchAnalytics();
  };

  const previewAnalytics = useMemo(() => buildPreviewAnalytics(agentId, selectedTimeframe), [agentId, selectedTimeframe]);
  const isCustomRangePending = selectedTimeframe === "custom" && (!startDate || !endDate || startDate > endDate);
  const liveAnalytics = currentAnalytics?.agentId === agentId && currentAnalytics.timeframe === selectedTimeframe ? currentAnalytics : null;
  const isPreviewEnabled = process.env.NODE_ENV !== "production" || searchParams.get("preview") === "true";
  const isPreview = isPreviewEnabled && !liveAnalytics && hasRequested && !isLoading && Boolean(error);
  const analytics = liveAnalytics || (isPreview || isCustomRangePending ? previewAnalytics : null);
  const isUsingPreviewData = analytics === previewAnalytics;
  const observedPoints = useMemo(() => toDashboardPoints(analytics?.timePoints || []), [analytics?.timePoints]);
  const points = useMemo(() => {
    if (!analytics) return [];
    if (isUsingPreviewData) return observedPoints;

    let rangeStart = analytics.startDate;
    let rangeEnd = analytics.endDate;
    if (getAnalyticsBucketSize(selectedTimeframe) === "day") {
      if (selectedTimeframe === "custom") {
        rangeStart = startDate;
        rangeEnd = endDate;
      } else {
        const daysByTimeframe: Partial<Record<AnalyticsTimeframe, number>> = { "3d": 2, "7d": 6, "30d": 29 };
        rangeEnd = new Date().toLocaleDateString("en-CA", { timeZone: timezone });
        const [year, month, day] = rangeEnd.split("-").map(Number);
        const rangeStartDate = new Date(year, month - 1, day - (daysByTimeframe[selectedTimeframe] || 0));
        rangeStart = `${rangeStartDate.getFullYear()}-${String(rangeStartDate.getMonth() + 1).padStart(2, "0")}-${String(rangeStartDate.getDate()).padStart(2, "0")}`;
      }
    }

    return toDashboardPoints(fillMissingAnalyticsBuckets(analytics.timePoints, selectedTimeframe, rangeStart, rangeEnd));
  }, [analytics, endDate, isUsingPreviewData, observedPoints, selectedTimeframe, startDate, timezone]);
  const chartData = useMemo(
    () =>
      points.map((point) => ({
        ...point,
        turnaroundBand: [point.turnaroundP50, point.turnaroundP95],
        ttsBand: [point.ttsP50, point.ttsP95],
        sttBand: [point.sttP50, point.sttP95],
      })),
    [points],
  );

  if (!analytics && error) {
    return (
      <DashboardErrorState
        message={error}
        onRetry={() => void handleRefresh()}
      />
    );
  }
  if (!analytics) return <DashboardSkeleton />;

  const gridColor = theme === "dark" ? "#334155" : "#E5E7EB";
  const tickColor = theme === "dark" ? "#94A3B8" : "#6B7280";
  const latestPoint = observedPoints[observedPoints.length - 1];
  const latestTurnaroundP95 = latestPoint?.turnaroundP95 || 0;

  const healthMetrics = latestPoint
    ? [
        { label: "TTS latency", value: latestPoint.ttsAverage, threshold: 450, format: formatMilliseconds },
        { label: "STT latency", value: latestPoint.sttAverage, threshold: 350, format: formatMilliseconds },
        { label: "Response latency p95", value: latestTurnaroundP95, threshold: 1200, format: formatMilliseconds },
        { label: "Session error rate", value: latestPoint.errorRate, threshold: 3, format: formatPercent },
      ]
    : [];

  const monitors = latestPoint
    ? [
        {
          name: "Voice response p95",
          scope: "env:production",
          value: formatMilliseconds(latestTurnaroundP95),
          threshold: "< 1.20s",
          status: getHealthStatus(latestTurnaroundP95, 1200),
        },
        {
          name: "Session error rate",
          scope: "channel:*",
          value: formatPercent(latestPoint.errorRate),
          threshold: "< 3.00%",
          status: getHealthStatus(latestPoint.errorRate, 3),
        },
        {
          name: "Speech-to-text latency",
          scope: "provider:*",
          value: formatMilliseconds(latestPoint.sttAverage),
          threshold: "< 350ms",
          status: getHealthStatus(latestPoint.sttAverage, 350),
        },
        {
          name: "Transfer rate",
          scope: "env:production",
          value: formatPercent(latestPoint.transferRate),
          threshold: "< 10.00%",
          status: getHealthStatus(latestPoint.transferRate, 10),
        },
      ]
    : [];

  const formatChartLabel = (value: string) => formatTooltipTime(value, timezone);
  const axisFormatter = (value: string) => formatAxisTime(value, selectedTimeframe, timezone);
  const selectedTimeframeLabel = timeframeOptions.find(({ value }) => value === selectedTimeframe)?.label || selectedTimeframe;
  const dashboardStatus = error ? "Stale" : selectedTimeframe === "custom" ? "Historical" : isAutoRefreshEnabled ? "Live" : "Paused";
  const isDashboardLive = dashboardStatus === "Live";
  const isDashboardStale = dashboardStatus === "Stale";

  return (
    <View className="min-h-full bg-gray-50 p-4 dark:bg-slate-950">
      <View className="mx-auto w-full max-w-[1800px] space-y-3">
        <View className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <View className="min-w-0">
            <View className="flex flex-wrap items-center gap-2">
              <Text className="text-lg font-semibold text-gray-950 dark:text-white">Analytics overview</Text>
              <View
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2 py-0.5",
                  isDashboardLive
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40"
                    : isDashboardStale
                      ? "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"
                      : "border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800",
                )}
              >
                <View className={cn("h-1.5 w-1.5 rounded-full", isDashboardLive ? "bg-emerald-500" : isDashboardStale ? "bg-amber-500" : "bg-gray-400")} />
                <Text
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider",
                    isDashboardLive
                      ? "text-emerald-700 dark:text-emerald-400"
                      : isDashboardStale
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-gray-600 dark:text-gray-300",
                  )}
                >
                  {dashboardStatus}
                </Text>
              </View>
              {(isPreview || isCustomRangePending) && (
                <View className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 dark:border-violet-900 dark:bg-violet-950/40">
                  <Text className="text-[10px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                    {isPreview ? "Preview data" : "Range pending"}
                  </Text>
                </View>
              )}
            </View>
            <Text className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
              Voice traffic, reliability, and conversational performance · Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </Text>
          </View>

          <View className="flex flex-wrap items-center gap-2">
            <DropdownField
              value={selectedTimeframe}
              onChange={(value) => handleTimeframeChange(value as AnalyticsTimeframe)}
              options={timeframeOptions}
              className="w-44"
            />
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => setIsAutoRefreshEnabled((isEnabled) => !isEnabled)}
              className="gap-1.5"
            >
              <View className={cn("h-1.5 w-1.5 rounded-full", isAutoRefreshEnabled ? "bg-emerald-500" : "bg-gray-400")} />
              60s
            </Button>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={handleRefresh}
              disabled={isLoading || isCustomRangePending}
            >
              <Icon
                name="refresh"
                size={13}
                className={cn(isLoading && "animate-spin")}
              />
              Refresh
            </Button>
          </View>
        </View>

        <View className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-slate-900 lg:flex-row lg:items-center lg:justify-between">
          <View className="flex flex-wrap items-center gap-2">
            <Text className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Scope</Text>
            {["env:*", "version:live", "channel:*", "region:*"].map((filter) => (
              <View
                key={filter}
                className="flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-slate-800"
              >
                <View className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                <Text className="text-[10px] font-medium text-gray-600 dark:text-gray-300">{filter}</Text>
              </View>
            ))}
            <button
              type="button"
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-medium text-violet-600 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-950/30"
            >
              <Icon
                name="add"
                size={11}
              />
              Add filter
            </button>
          </View>
          <Text className="text-[10px] text-gray-400 dark:text-gray-500">
            {selectedTimeframeLabel} · {timezone}
          </Text>
        </View>

        {selectedTimeframe === "custom" && (
          <View className="flex flex-wrap items-end gap-3 rounded-md border border-violet-200 bg-violet-50/60 p-3 dark:border-violet-900 dark:bg-violet-950/20">
            <DateField
              label="From"
              value={pendingStartDate}
              onChange={(event) => setPendingStartDate(event.target.value)}
              max={pendingEndDate || new Date().toISOString().split("T")[0]}
            />
            <DateField
              label="To"
              value={pendingEndDate}
              onChange={(event) => setPendingEndDate(event.target.value)}
              min={pendingStartDate || undefined}
              max={new Date().toISOString().split("T")[0]}
            />
            <Button
              variant="brand"
              size="sm"
              onClick={handleApplyCustomRange}
              isDisabled={!pendingStartDate || !pendingEndDate || isCustomDateRangeInvalid}
            >
              Apply range
            </Button>
            {isCustomDateRangeInvalid && <Text className="pb-2 text-xs text-red-600 dark:text-red-400">The end date must be on or after the start date.</Text>}
          </View>
        )}

        {error && (
          <View className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/30">
            <View className="flex items-center gap-2">
              <Icon
                name="warning-amber"
                size={14}
                className="text-amber-600 dark:text-amber-400"
              />
              <Text className="text-xs text-amber-800 dark:text-amber-300">
                {isPreview
                  ? "Live analytics are unavailable, so this dashboard is showing deterministic preview data."
                  : "The latest refresh failed. Showing the last available data."}
              </Text>
            </View>
            <Text className="hidden max-w-md truncate text-[10px] text-amber-600 dark:text-amber-500 md:block">{error}</Text>
          </View>
        )}

        <View className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <MetricCard
            label="Sessions"
            value={analytics.totalSessions.toLocaleString()}
            detail="Total voice sessions started in the selected time window."
            delta={compareHalves(points, (point) => point.sessions, "sum")}
            icon="phone"
            color={ANALYTICS_COLORS.primary}
            data={points}
            dataKey="sessions"
          />
          <MetricCard
            label="Error rate"
            value={formatPercent(analytics.metrics.errorRate)}
            detail="Percentage of sessions with one or more runtime errors."
            delta={compareHalves(points, (point) => point.errorRate)}
            isLowerBetter
            icon="error"
            color={ANALYTICS_COLORS.red}
            data={points}
            dataKey="errorRate"
          />
          <MetricCard
            label="Avg response"
            value={formatMilliseconds(analytics.metrics.avgTurnAroundTimeMs)}
            detail="Average time from the end of user speech to the start of agent audio."
            delta={compareHalves(points, (point) => point.turnaroundAverage)}
            isLowerBetter
            icon="speed"
            color={ANALYTICS_COLORS.blue}
            data={points}
            dataKey="turnaroundAverage"
          />
          <MetricCard
            label="Avg duration"
            value={formatMinutes(analytics.metrics.avgSessionLengthMinutes)}
            detail="Average end-to-end session duration."
            delta={compareHalves(points, (point) => point.sessionLengthAverage)}
            icon="clock"
            color={ANALYTICS_COLORS.cyan}
            data={points}
            dataKey="sessionLengthAverage"
          />
          <MetricCard
            label="Transfer rate"
            value={formatPercent(analytics.metrics.transferRate)}
            detail="Percentage of sessions transferred to a person or external destination."
            delta={compareHalves(points, (point) => point.transferRate)}
            isLowerBetter
            icon="phone-forward"
            color={ANALYTICS_COLORS.amber}
            data={points}
            dataKey="transferRate"
          />
          <MetricCard
            label="Turns / session"
            value={analytics.metrics.avgTurnDepth.toFixed(1)}
            detail="Average number of back-and-forth conversation turns per session."
            delta={compareHalves(points, (point) => point.turnDepthAverage)}
            icon="message"
            color={ANALYTICS_COLORS.pink}
            data={points}
            dataKey="turnDepthAverage"
          />
        </View>

        <View className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <DashboardPanel
            title="Traffic & reliability"
            description="Session volume and error rate over the selected period."
            query="sum:agent.sessions{env:*} · avg:agent.error_rate{env:*}"
            className="lg:col-span-8"
          >
            <View className="h-[286px] px-2 pb-2 pt-3">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 10, left: -10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="session-volume-fill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={ANALYTICS_COLORS.primary}
                        stopOpacity={0.34}
                      />
                      <stop
                        offset="100%"
                        stopColor={ANALYTICS_COLORS.primary}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke={gridColor}
                    strokeOpacity={0.55}
                  />
                  <XAxis
                    dataKey="time"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={axisFormatter}
                  />
                  <YAxis
                    yAxisId="sessions"
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={formatCompactNumber}
                  />
                  <YAxis
                    yAxisId="rate"
                    orientation="right"
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={(value: number) => `${value.toFixed(0)}%`}
                  />
                  <RechartsTooltip
                    content={<AnalyticsChartTooltip formatLabel={formatChartLabel} />}
                    cursor={{ stroke: tickColor, strokeDasharray: "3 3", strokeOpacity: 0.45 }}
                  />
                  <Area
                    yAxisId="sessions"
                    type="monotone"
                    dataKey="sessions"
                    name="Sessions"
                    stroke={ANALYTICS_COLORS.primary}
                    strokeWidth={2}
                    fill="url(#session-volume-fill)"
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="rate"
                    type="monotone"
                    dataKey="errorRate"
                    name="Error rate"
                    stroke={ANALYTICS_COLORS.red}
                    strokeWidth={1.8}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </View>
            <View className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-4 py-2 dark:border-gray-800">
              <LegendItem
                color={ANALYTICS_COLORS.primary}
                label="Sessions"
              />
              <LegendItem
                color={ANALYTICS_COLORS.red}
                label="Error rate"
              />
            </View>
          </DashboardPanel>

          <DashboardPanel
            title="Health snapshot"
            description="Current values evaluated against recommended operational thresholds."
            query="latest:agent.health{env:*}"
            className="lg:col-span-4"
          >
            <View className="px-4 py-1">
              {healthMetrics.length > 0 ? (
                healthMetrics.map((metric) => {
                  const status = getHealthStatus(metric.value, metric.threshold);
                  return (
                    <HealthMetricRow
                      key={metric.label}
                      label={metric.label}
                      value={metric.format(metric.value)}
                      threshold={metric.format(metric.threshold)}
                      status={status}
                      score={getHealthScore(metric.value, metric.threshold)}
                    />
                  );
                })
              ) : (
                <View className="flex h-[280px] flex-col items-center justify-center gap-2">
                  <Icon
                    name="activity"
                    size={22}
                    className="text-gray-300 dark:text-gray-700"
                  />
                  <Text className="text-xs text-gray-400 dark:text-gray-500">No samples in this time window</Text>
                </View>
              )}
            </View>
          </DashboardPanel>

          <DashboardPanel
            title="Latency percentiles"
            description="Response latency and speech pipeline percentiles for each time bucket."
            query={latencyView === "end-to-end" ? "p50,p95:agent.turnaround_ms{env:*}" : "avg,p95:agent.speech_latency_ms{env:*} by {stage}"}
            className="lg:col-span-8"
            action={
              <View className="flex rounded border border-gray-200 bg-gray-50 p-0.5 dark:border-gray-700 dark:bg-slate-800">
                {(["end-to-end", "pipeline"] as const).map((view) => (
                  <button
                    key={view}
                    type="button"
                    onClick={() => setLatencyView(view)}
                    className={cn(
                      "rounded px-2 py-1 text-[10px] font-medium transition-colors",
                      latencyView === view
                        ? "bg-white text-violet-700 shadow-sm dark:bg-slate-700 dark:text-violet-300"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
                    )}
                  >
                    {view === "end-to-end" ? "End-to-end" : "Pipeline"}
                  </button>
                ))}
              </View>
            }
          >
            <View className="h-[270px] px-2 pb-2 pt-3">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke={gridColor}
                    strokeOpacity={0.55}
                  />
                  <XAxis
                    dataKey="time"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={axisFormatter}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={54}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={formatMilliseconds}
                  />
                  <RechartsTooltip
                    content={<AnalyticsChartTooltip formatLabel={formatChartLabel} />}
                    cursor={{ stroke: tickColor, strokeDasharray: "3 3", strokeOpacity: 0.45 }}
                  />
                  {latencyView === "end-to-end" ? (
                    <>
                      <Area
                        type="monotone"
                        dataKey="turnaroundBand"
                        name="Response p50–p95"
                        stroke="none"
                        fill={ANALYTICS_COLORS.primaryLight}
                        fillOpacity={0.18}
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="turnaroundP50"
                        name="Response p50"
                        stroke={ANALYTICS_COLORS.primary}
                        strokeWidth={1.8}
                        dot={false}
                        activeDot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="turnaroundP95"
                        name="Response p95"
                        stroke={ANALYTICS_COLORS.primaryLight}
                        strokeWidth={1.6}
                        strokeDasharray="5 3"
                        dot={false}
                        activeDot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                    </>
                  ) : (
                    <>
                      <Area
                        type="monotone"
                        dataKey="ttsBand"
                        name="TTS p50–p95"
                        stroke="none"
                        fill={ANALYTICS_COLORS.blue}
                        fillOpacity={0.1}
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="sttBand"
                        name="STT p50–p95"
                        stroke="none"
                        fill={ANALYTICS_COLORS.cyan}
                        fillOpacity={0.1}
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="ttsAverage"
                        name="TTS latency"
                        stroke={ANALYTICS_COLORS.blue}
                        strokeWidth={1.8}
                        dot={false}
                        activeDot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="sttAverage"
                        name="STT latency"
                        stroke={ANALYTICS_COLORS.cyan}
                        strokeWidth={1.8}
                        dot={false}
                        activeDot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </View>
            <View className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-4 py-2 dark:border-gray-800">
              {latencyView === "end-to-end" ? (
                <>
                  <LegendItem
                    color={ANALYTICS_COLORS.primary}
                    label="Response p50"
                  />
                  <LegendItem
                    color={ANALYTICS_COLORS.primaryLight}
                    label="Response p95"
                    isDashed
                  />
                </>
              ) : (
                <>
                  <LegendItem
                    color={ANALYTICS_COLORS.blue}
                    label="TTS latency"
                  />
                  <LegendItem
                    color={ANALYTICS_COLORS.cyan}
                    label="STT latency"
                  />
                </>
              )}
            </View>
          </DashboardPanel>

          <DashboardPanel
            title="Conversation depth"
            description="Average session length and conversational turn depth."
            query="avg:agent.session_length{env:*} · avg:agent.turn_depth{env:*}"
            className="lg:col-span-4"
          >
            <View className="h-[270px] px-2 pb-2 pt-3">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 4, left: -8, bottom: 0 }}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke={gridColor}
                    strokeOpacity={0.55}
                  />
                  <XAxis
                    dataKey="time"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={38}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={axisFormatter}
                  />
                  <YAxis
                    yAxisId="duration"
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={(value: number) => `${value.toFixed(0)}m`}
                  />
                  <YAxis
                    yAxisId="turns"
                    orientation="right"
                    tickLine={false}
                    axisLine={false}
                    width={30}
                    tick={{ fill: tickColor, fontSize: 10 }}
                  />
                  <RechartsTooltip
                    content={<AnalyticsChartTooltip formatLabel={formatChartLabel} />}
                    cursor={{ stroke: tickColor, strokeDasharray: "3 3", strokeOpacity: 0.45 }}
                  />
                  <Area
                    yAxisId="duration"
                    type="monotone"
                    dataKey="sessionLengthAverage"
                    name="Avg duration"
                    stroke={ANALYTICS_COLORS.cyan}
                    fill={ANALYTICS_COLORS.cyan}
                    fillOpacity={0.12}
                    strokeWidth={1.8}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="turns"
                    type="monotone"
                    dataKey="turnDepthAverage"
                    name="Turns / session"
                    stroke={ANALYTICS_COLORS.pink}
                    strokeWidth={1.8}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </View>
            <View className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-4 py-2 dark:border-gray-800">
              <LegendItem
                color={ANALYTICS_COLORS.cyan}
                label="Avg duration"
              />
              <LegendItem
                color={ANALYTICS_COLORS.pink}
                label="Turns / session"
              />
            </View>
          </DashboardPanel>

          <DashboardPanel
            title="Reliability rates"
            description="Operational error and human-transfer rates by time bucket."
            query="avg:agent.error_rate{env:*} · avg:agent.transfer_rate{env:*}"
            className="lg:col-span-6"
          >
            <View className="h-[245px] px-2 pb-2 pt-3">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 10, left: -6, bottom: 0 }}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke={gridColor}
                    strokeOpacity={0.55}
                  />
                  <XAxis
                    dataKey="time"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={axisFormatter}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={42}
                    tick={{ fill: tickColor, fontSize: 10 }}
                    tickFormatter={(value: number) => `${value.toFixed(0)}%`}
                  />
                  <RechartsTooltip
                    content={<AnalyticsChartTooltip formatLabel={formatChartLabel} />}
                    cursor={{ stroke: tickColor, strokeDasharray: "3 3", strokeOpacity: 0.45 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="errorRate"
                    name="Error rate"
                    stroke={ANALYTICS_COLORS.red}
                    strokeWidth={1.8}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="transferRate"
                    name="Transfer rate"
                    stroke={ANALYTICS_COLORS.amber}
                    strokeWidth={1.8}
                    dot={false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </View>
            <View className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-4 py-2 dark:border-gray-800">
              <LegendItem
                color={ANALYTICS_COLORS.red}
                label="Error rate"
              />
              <LegendItem
                color={ANALYTICS_COLORS.amber}
                label="Transfer rate"
              />
            </View>
          </DashboardPanel>

          <DashboardPanel
            title="Monitor status"
            description="Threshold monitors for the most important agent signals."
            query="monitors:agent-runtime status:(ok OR warn OR alert)"
            className="lg:col-span-6"
          >
            <View className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <th className="px-4 py-2 text-left text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Monitor</th>
                    <th className="hidden px-3 py-2 text-left text-[9px] font-semibold uppercase tracking-wider text-gray-400 sm:table-cell dark:text-gray-500">
                      Scope
                    </th>
                    <th className="px-3 py-2 text-right text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Current</th>
                    <th className="hidden px-3 py-2 text-right text-[9px] font-semibold uppercase tracking-wider text-gray-400 md:table-cell dark:text-gray-500">
                      Threshold
                    </th>
                    <th className="px-4 py-2 text-right text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {monitors.length > 0 ? (
                    monitors.map((monitor) => {
                      const statusLabel = monitor.status === "healthy" ? "OK" : monitor.status === "warning" ? "WARN" : "ALERT";
                      return (
                        <tr
                          key={monitor.name}
                          className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-slate-800/60"
                        >
                          <td className="px-4 py-3">
                            <View className="flex items-center gap-2">
                              <View
                                className={cn(
                                  "h-2 w-2 shrink-0 rounded-full",
                                  monitor.status === "healthy" ? "bg-emerald-500" : monitor.status === "warning" ? "bg-amber-500" : "bg-red-500",
                                )}
                              />
                              <Text className="whitespace-nowrap text-[11px] font-medium text-gray-800 dark:text-gray-200">{monitor.name}</Text>
                            </View>
                          </td>
                          <td className="hidden px-3 py-3 sm:table-cell">
                            <Text className="whitespace-nowrap text-[10px] text-gray-400 dark:text-gray-500">{monitor.scope}</Text>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <Text className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-gray-900 dark:text-white">{monitor.value}</Text>
                          </td>
                          <td className="hidden px-3 py-3 text-right md:table-cell">
                            <Text className="whitespace-nowrap text-[10px] tabular-nums text-gray-400 dark:text-gray-500">{monitor.threshold}</Text>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Text
                              className={cn(
                                "inline-flex rounded px-1.5 py-0.5 text-[9px] font-bold",
                                monitor.status === "healthy"
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                                  : monitor.status === "warning"
                                    ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
                                    : "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
                              )}
                            >
                              {statusLabel}
                            </Text>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="h-[190px] px-4 text-center"
                      >
                        <Text className="text-xs text-gray-400 dark:text-gray-500">No monitor data in this time window</Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </View>
            <View className="flex items-center justify-between border-t border-gray-100 px-4 py-2 dark:border-gray-800">
              <Text className="text-[10px] text-gray-400 dark:text-gray-500">Evaluated every 60 seconds</Text>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/agents/${agentId}/sessions`)}
                className="text-[10px]"
              >
                Investigate sessions
                <Icon
                  name="arrow-right"
                  size={11}
                />
              </Button>
            </View>
          </DashboardPanel>
        </View>

        <View className="flex flex-col gap-1 border-t border-gray-200 py-3 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <Text className="text-[10px] text-gray-400 dark:text-gray-500">
            Window: {formatTooltipTime(analytics.startDate, timezone)} – {formatTooltipTime(analytics.endDate, timezone)}
          </Text>
          <Text className="text-[10px] text-gray-400 dark:text-gray-500">All percentile bands show p50–p95 · Times shown in {timezone}</Text>
        </View>
      </View>
    </View>
  );
};
